import { useEffect, useRef, useState } from "react";
import { request } from "../api/client";
import { ErrorNotice } from "../components/AdminFeedback";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { ValidatedForm } from "../components/ValidatedForm";
import { useLocale } from "../i18n";
import { useSetupMutation } from "./useSetupMutation";
import type { RecordValue } from "./types";

type Question = RecordValue & { id: string; label: string };
type Form = { version: number; unitVersion: number; definition: RecordValue & { method: string; criteria: Question[] } };

export function RoundSurveyContentEditor({ roundId, roleCode, roleName, disabled, onChanged, onLock }: {
  roundId: string; roleCode: string; roleName: string; disabled: boolean;
  onChanged: () => void; onLock: (locked: boolean) => void;
}) {
  const { w } = useLocale();
  const [opened, setOpened] = useState(false);
  const [form, setForm] = useState<Form>();
  const [unitVersion, setUnitVersion] = useState<number>();
  const [error, setError] = useState<unknown>();
  const [revision, refresh] = useState(0);
  const mutation = useSetupMutation(() => { setOpened(false); setForm(undefined); onChanged(); });
  const locked = mutation.busy || mutation.uncertain;
  const lockCallback = useRef(onLock); lockCallback.current = onLock;
  useEffect(() => { lockCallback.current(locked); return () => lockCallback.current(false); }, [locked]);
  useEffect(() => {
    if (!opened) return;
    const controller = new AbortController(); setForm(undefined); setUnitVersion(undefined); setError(undefined);
    request<Form>("/events/eventEvaluationForm?" + new URLSearchParams({ roundId, roleCode }), "GET", undefined, controller.signal).then(saved => {
      if (controller.signal.aborted) return;
      if (saved.definition.method !== "QUESTIONNAIRE") throw new Error(w("نموذج الاستبيان غير متاح.", "The survey form is unavailable."));
      if (!Number.isSafeInteger(saved.unitVersion)) throw new Error(w("حدّث الصفحة قبل تعديل النصوص.", "Refresh the page before editing wording."));
      setForm(saved); setUnitVersion(saved.unitVersion);
    }).catch(e => { if (!controller.signal.aborted) setError(e); });
    return () => controller.abort();
  }, [opened, roundId, roleCode, revision]);

  return <section className="competition-survey-editor">
    {!opened && <button type="button" className="secondary" disabled={disabled} onClick={() => setOpened(true)}>{w(`تعديل نصوص أسئلة ${roleName}`, `Edit ${roleName} question text`)}</button>}
    {opened && <>
      <h4>{w(`نصوص أسئلة ${roleName}`, `${roleName} question text`)}</h4>
      <p className="info-box">{w("يُحفظ النص لهذه الجولة فقط، حتى أثناء التصويت. يبقى عدد الأسئلة وترتيبها وأوزانها ونطاق درجاتها والإجابات والنتائج كما هي.", "Wording is saved for this round only, including during voting. Question count, order, weights, score ranges, answers and results stay unchanged.")}</p>
      <ErrorNotice operation="read" error={error} retry={() => refresh(n => n + 1)} />
      <ErrorNotice error={mutation.error} retry={mutation.uncertain ? () => void mutation.send() : undefined} />
      {!form && !error && <LoadingSkeleton variant="form" label={w("جارٍ تحميل أسئلة الجولة…", "Loading round questions…")} />}
      {form && unitVersion !== undefined && <ValidatedForm valid={form.definition.criteria.every(q => !!q.label.trim())} onSubmit={e => {
        e.preventDefault();
        void mutation.send("/events/eventEvaluationForm?" + new URLSearchParams({ roundId, roleCode }), "PUT", {
          expectedVersion: unitVersion,
          ...form.definition,
          criteria: form.definition.criteria.map(q => ({ ...q, label: q.label.trim() })),
        });
      }}><fieldset disabled={locked || disabled}>
        <div className="competition-questions">{form.definition.criteria.map((q, i) => <label key={q.id} className="competition-survey-question">
          <span>{w(`نص السؤال ${i + 1}`, `Question ${i + 1} text`)}</span>
          <input required maxLength={200} value={q.label} onChange={e => setForm({ ...form, definition: { ...form.definition, criteria: form.definition.criteria.map((row, index) => index === i ? { ...row, label: e.target.value } : row) } })} />
        </label>)}</div>
        <footer><button className="primary" type="submit">{mutation.busy ? w("جارٍ الحفظ…", "Saving…") : w("حفظ النصوص", "Save wording")}</button></footer>
      </fieldset></ValidatedForm>}
      <button type="button" className="secondary" disabled={locked} onClick={() => { setOpened(false); setForm(undefined); mutation.resetFeedback(); }}>{w("إلغاء", "Cancel")}</button>
    </>}
  </section>;
}
